import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import { Col, Form, Row } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  Popup,
  AtsButtonMenu,
  Typography,
  TYPOGRAPHY_WEIGHT,
  TimelineItem,
  Card,
  SelectSmall,
  PLACEMENTS,
  Button,
  BUTTON_TYPES,
  Divider,
  RichArea,
  Select,
  notification,
  NOTIFICATION_TYPES,
} from '_atoms';
import {
  PopupAttachFile,
  POPUP_ATTACH_TIMELINE_FORMATS,
  PopupCompleteReminder,
  PopupCreateInteraction,
  PopupCreateReminder,
} from '_molecules';
import { ACTIONS, Can, UI } from 'permission';
import { getCandidateReminders, getCandidateTimeline } from 'store/selectors/candidateSelectors';
import { BUTTON_TEXT } from 'constants/text';
import {
  completeReminder,
  deleteInteraction,
  deleteReminder,
  getMentionUsers,
  getReminders,
  getTimeline,
  postCommentTimeline,
} from 'actions';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { ReactComponent as Sort } from 'dist/icons/arrow_ascending_descending.svg';
import { ReactComponent as IconAttach } from 'dist/icons/attach.svg';
import { setCandidateTimeline, setCandidateTimelineFilters, setRemindersList } from 'store/reducers/candidateReducer';
import { getJobTimelineCommentLoading, getTimelineLoading } from 'store/selectors/jobSelectors';
import { useAppDispatch } from 'store';
import type { CandidateData, Reminder } from 'store/reducers/candidateReducer';
import type { TimelineItemData } from '_atoms/TimelineItem';
import type { Uuid } from 'store/reducers/types';
import { CommentAttachedFilesList } from './CommentAttachedFilesList';
import './style.scss';

const TIMELINE_COMMENT_MAX_FILES = 5;
const TIMELINE_COMMENT_MAX_FILE_BYTES = 50 * 1024 * 1024;
const TIMELINE_PASTE_IMAGE_MIME = ['image/png', 'image/jpeg'];

const SORTING_OPTIONS = [
  { value: 'desc', label: 'Newest first' },
  { value: 'asc', label: 'Oldest first' },
];

const FILTER_OPTIONS = [
  { value: '', label: 'All activities' },
  { value: 'interactions', label: 'Interactions' },
  { value: 'job_updates', label: 'Job updates' },
  { value: 'profile', label: 'Profile' },
  { value: 'status', label: 'Status' },
];

const initialValues = {
  sorting: SORTING_OPTIONS[0],
  filter: FILTER_OPTIONS[0],
};
const initialValuesFeed = {
  sorting: SORTING_OPTIONS[0],
};

interface SortOption {
  value: string;
  label: string;
}

interface SortingButtonInputProps {
  value?: SortOption;
  onChange?: (option: SortOption) => void;
  options: SortOption[];
  [key: string]: unknown;
}

const SortingButtonInput: React.FC<SortingButtonInputProps> = ({ value, onChange, options, ...props }) => (
  <button
    className="sorting-button"
    type="button"
    onClick={() => onChange?.(options[value?.value === options[0].value ? 1 : 0])}
    {...props}
  >
    {value?.label}
    <Sort className={value?.value} />
  </button>
);

interface SortingButtonProps {
  itemProps: { name: string; [key: string]: unknown };
  options: SortOption[];
}

const SortingButton: React.FC<SortingButtonProps> = ({ itemProps, ...props }) => (
  <Form.Item {...itemProps}>
    <SortingButtonInput {...props} />
  </Form.Item>
);

interface MentionUserOption {
  uuid: Uuid;
  full_name: string;
}

const TimelineComment: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { id: jobUuid, candidate: candidateUuid } = useParams<{ id: string; candidate: string }>();

  const loading = useSelector(getJobTimelineCommentLoading);

  const [mentionUsersOptions, setMentionUsersOptions] = useState<MentionUserOption[]>([]);
  const [initialComment, setInitialComment] = useState('');
  const [comment, setComment] = useState('');
  const [openAttachPopup, setOpenAttachPopup] = useState(false);

  const attachedFiles: UploadFile[] = Form.useWatch('files', form) || [];

  const loadUsers = () => {
    setMentionUsersOptions([]);
    getMentionUsers(jobUuid, candidateUuid).then(users => {
      setMentionUsersOptions(users as unknown as MentionUserOption[]);
    });
  };

  const onPostComment = ({ mention_users }: { mention_users?: Array<{ value: Uuid }> }) => {
    const formData = new FormData();
    formData.append('comment', comment);
    formData.append('mention_users', (mention_users?.map(({ value }) => value) || []) as unknown as string);
    if (attachedFiles && attachedFiles.length) {
      attachedFiles.forEach(file => {
        if (file.originFileObj) formData.append('attachments', file.originFileObj);
      });
    }

    dispatch(postCommentTimeline(formData as unknown as Record<string, unknown>, jobUuid, candidateUuid)).then(() => {
      setInitialComment(' '); // crutch because the input field is not controlled
      setInitialComment('');
      form.resetFields();
    });
  };

  const onOpenAttachPopup = () => setOpenAttachPopup(true);
  const onCloseAttachPopup = () => setOpenAttachPopup(false);

  const onAttachFromPopup = ({ files: nextFiles }: { files: UploadFile[] }) => {
    form.setFieldsValue({ files: nextFiles });
    onCloseAttachPopup();
  };

  const onPasteImageFiles = useCallback(
    (files: File[]) => {
      const current: UploadFile[] = form.getFieldValue('files') || [];
      if (current.length >= TIMELINE_COMMENT_MAX_FILES) {
        notification({
          type: NOTIFICATION_TYPES.ERROR,
          message: `You can attach up to ${TIMELINE_COMMENT_MAX_FILES} files only.`,
        });
        return;
      }

      const added: UploadFile[] = [];
      let hitLimit = false;

      files.forEach(file => {
        if (current.length + added.length >= TIMELINE_COMMENT_MAX_FILES) {
          hitLimit = true;
          return;
        }

        let f: File = file;
        if (!TIMELINE_PASTE_IMAGE_MIME.includes(f.type)) {
          if (f.size > 0 && (!f.type || f.type === 'application/octet-stream')) {
            f = new File([f], f.name || `Screenshot-${Date.now()}.png`, { type: 'image/png' });
          } else {
            notification({
              type: NOTIFICATION_TYPES.ERROR,
              message: 'Pasted images must be JPG or PNG.',
            });
            return;
          }
        }

        if (f.size > TIMELINE_COMMENT_MAX_FILE_BYTES) {
          notification({
            type: NOTIFICATION_TYPES.ERROR,
            message: 'File size exceeds 50 MB limit.',
          });
          return;
        }

        const name = f.name?.trim() || `Screenshot-${Date.now()}.png`;
        added.push({
          uid: `paste-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          name,
          status: 'done',
          originFileObj: f as UploadFile['originFileObj'],
        });
      });

      if (hitLimit) {
        notification({
          type: NOTIFICATION_TYPES.ERROR,
          message: `You can attach up to ${TIMELINE_COMMENT_MAX_FILES} files only.`,
        });
      }

      if (added.length) {
        form.setFieldsValue({ files: [...current, ...added] });
      }
    },
    [form]
  );

  return (
    <div>
      <Form
        className="timeline__comment"
        name="timeline-comment"
        form={form}
        initialValues={{ files: [] }}
        onFinish={onPostComment}
      >
        <Row gutter={[32, 0]}>
          <Col lg={12} md={12} sm={24}>
            <Select
              labelInValue
              label="Mention user(s)"
              mode={SELECT_TYPES.MULTIPLE}
              options={mentionUsersOptions as unknown as { label: string; value: string }[]}
              onDropdownVisibleChange={open => {
                if (open) loadUsers();
              }}
              fieldNames={{ value: 'uuid', label: 'full_name' }}
              itemProps={{ name: 'mention_users' }}
            />
          </Col>
        </Row>
        <Row gutter={[32, 0]}>
          <Col lg={24} md={24} sm={24}>
            <RichArea
              label="Comment"
              maxLength={6000}
              placeholder="Enter comment"
              onChange={setComment}
              initialValue={initialComment}
              onPasteImageFiles={onPasteImageFiles}
            />
            <Form.Item noStyle name="files" initialValue={[]}>
              <CommentAttachedFilesList />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="end" gutter={8}>
          <Col>
            <Button type={BUTTON_TYPES.SECONDARY} onClick={onOpenAttachPopup} loading={loading} icon={<IconAttach />}>
              Attach
            </Button>
          </Col>
          <Col>
            <Button htmlType="submit" type={BUTTON_TYPES.SECONDARY} disabled={!comment} loading={loading}>
              Post comment
            </Button>
          </Col>
        </Row>
      </Form>
      <PopupAttachFile
        open={openAttachPopup}
        onClose={onCloseAttachPopup}
        list={attachedFiles}
        onAttach={onAttachFromPopup}
        allowedFormats={POPUP_ATTACH_TIMELINE_FORMATS}
      />
    </div>
  );
};

interface RemindersProps {
  initials: string;
  reminderData: unknown;
  setReminderData: (data: unknown) => void;
  setOpenReminderModal: (open: boolean) => void;
}

const Reminders: React.FC<RemindersProps> = ({ initials, reminderData, setReminderData, setOpenReminderModal }) => {
  const dispatch = useAppDispatch();
  const { id, candidate } = useParams<{ id: string; candidate: string }>();
  const candidateId = candidate || id;

  const reminders = useSelector(getCandidateReminders);

  const [isAllReminders, setIsAllReminders] = useState(false);

  const [openComplete, setOpenComplete] = useState(false);
  const [deleteReminderData, setDeleteReminderData] = useState<Reminder | null>(null);

  const remindersToShow = useMemo(() => (isAllReminders ? reminders : [reminders[0]]), [isAllReminders, reminders]);

  const onClickAllReminders = () => setIsAllReminders(a => !a);

  const onActionClick = (type: string, data: Reminder) => {
    switch (type) {
      case 'reminder-edit':
        setReminderData({
          date: moment(data.datetime),
          time: moment(data.datetime).format('HH:mm'),
          candidate: initials,
          comment: data.comment,
          type: data.type,
          uuid: data.uuid,
          create_google_event: data.create_google_event,
          recruiter: data.recruiter.uuid,
        });
        setOpenReminderModal(true);
        break;
      case 'reminder-complete':
        setReminderData(data);
        setOpenComplete(true);
        break;
      case 'reminder-delete':
        setDeleteReminderData(data);
    }
  };

  if (!reminders.length) return null;

  return (
    <div className="reminders">
      <Typography.Title3 className="reminders__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {`Reminder${reminders.length > 1 ? 's' : ''}`}
        <span>({reminders.length})</span>
      </Typography.Title3>
      {remindersToShow.map(data => (
        <TimelineItem key={data.uuid} data={data as unknown as TimelineItemData} onAction={onActionClick as never} />
      ))}
      {reminders.length > 1 && (
        <Button type={BUTTON_TYPES.TEXT} className="reminders__see-all" onClick={onClickAllReminders}>
          {`Show ${isAllReminders ? 'less' : 'more'} reminders `}
        </Button>
      )}
      <PopupCompleteReminder
        open={openComplete}
        onSave={(comment: string) => {
          dispatch(completeReminder(reminderData as Reminder, candidateId, comment));
          setOpenComplete(false);
          setReminderData(null);
        }}
        onClose={() => {
          setOpenComplete(false);
          setReminderData(null);
        }}
      />
      <Popup
        open={!!deleteReminderData}
        title="Delete reminder"
        cancelButtonProps={{ onClick: () => setDeleteReminderData(null), children: BUTTON_TEXT.CANCEL }}
        okButtonProps={{
          onClick: () => {
            setDeleteReminderData(null);
            dispatch(deleteReminder(deleteReminderData?.uuid as Uuid, candidateId));
          },
          children: BUTTON_TEXT.DELETE,
        }}
      >
        Do you really want to delete reminder?
      </Popup>
    </div>
  );
};

interface TimelineProps {
  candidateData: Partial<CandidateData>;
  isActivityFeed?: boolean;
}

export const Timeline: React.FC<TimelineProps> = ({ candidateData, isActivityFeed }) => {
  const dispatch = useAppDispatch();
  const { id, candidate } = useParams<{ id: string; candidate: string }>();

  const initials = useMemo(() => `${candidateData.first_name_latin} ${candidateData.last_name_latin}`, [candidateData]);

  const [form] = Form.useForm();

  const timeline = useSelector(getCandidateTimeline);
  const reminders = useSelector(getCandidateReminders);
  const loading = useSelector(getTimelineLoading);

  const [openReminderModal, setOpenReminderModal] = useState(false);
  const [reminderData, setReminderData] = useState<unknown>(null);
  const [newInteractionModal, openInteractionModal] = useState(false);
  const [dataInteraction, changeDataInteraction] = useState<unknown>(null);
  const [deleteInteractionUuid, setDeleteInteractionUuid] = useState<Uuid | null>(null);

  const closeInteractionPopup = () => {
    openInteractionModal(false);
    changeDataInteraction(null);
  };

  const closeReminderPopup = () => {
    setOpenReminderModal(false);
    setReminderData(null);
  };

  const onNewInteraction = () => {
    openInteractionModal(true);
  };

  const onActionClick = (type: string, data: TimelineItemData) => {
    const { details } = data;
    switch (type) {
      case 'interaction-edit':
        changeDataInteraction({
          uuid: details?.uuid,
          date: moment(details?.datetime),
          time: moment(details?.datetime).format('HH:mm'),
          type: details?.type,
          activity: details?.activity,
          recruiter: details?.recruiter?.uuid,
          comment: details?.comment,
        });
        openInteractionModal(true);
        break;
      case 'interaction-delete':
        setDeleteInteractionUuid(details?.uuid as Uuid);
    }
  };

  const hasMore = timeline.count > timeline.data.length;

  const onLoadMore = (clear?: boolean) => dispatch(getTimeline(candidate || id, candidate ? id : candidate, clear));

  const onChangeFilters = (_: unknown, values: { sorting: SortOption; filter?: SortOption }) => {
    const sorting = values.sorting.value;
    const filter = values.filter?.value;
    dispatch(setCandidateTimelineFilters({ filter, sorting } as { filter: string; sorting: 'asc' | 'desc' }));
    onLoadMore(true);
  };

  useEffect(() => {
    dispatch(
      setCandidateTimelineFilters({
        sorting: SORTING_OPTIONS[0].value,
        filter: FILTER_OPTIONS[0].value,
      } as { filter: string; sorting: 'asc' | 'desc' })
    );
    form.resetFields();
    if (!candidate) dispatch(getReminders(id));
    onLoadMore(true);
    return () => {
      dispatch(setRemindersList([]));
      dispatch(setCandidateTimeline({ clear: true }));
      dispatch(
        setCandidateTimelineFilters({
          sorting: SORTING_OPTIONS[0].value,
          filter: FILTER_OPTIONS[0].value,
        } as { filter: string; sorting: 'asc' | 'desc' })
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, candidate]);

  return (
    <Card className={cn('timeline', { 'activity-feed': isActivityFeed })}>
      <div className="timeline__header">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD} className="title">
          {isActivityFeed ? 'Activity feed' : 'Timeline'}
          <span>{timeline.count + reminders.length}</span>
        </Typography.Title2>
        <div className="actions">
          <Form
            name="timeline"
            form={form}
            initialValues={isActivityFeed ? initialValuesFeed : initialValues}
            onValuesChange={onChangeFilters}
          >
            <SortingButton itemProps={{ name: 'sorting' }} options={SORTING_OPTIONS} />
            {!isActivityFeed && (
              <>
                <Divider type="vertical" />
                <SelectSmall
                  label="Show"
                  itemProps={{ name: 'filter' }}
                  options={FILTER_OPTIONS}
                  placement={PLACEMENTS.BOTTOM_RIGHT}
                  value={undefined as never}
                  onChange={undefined as never}
                />
              </>
            )}
          </Form>
          <Can I={ACTIONS.READ} a={UI.EDIT_CANDIDATE_TIMELINE} passThrough>
            {access =>
              access &&
              !isActivityFeed && (
                <AtsButtonMenu>
                  <li role="none" onClick={onNewInteraction}>
                    New interaction
                  </li>
                  <li role="none" onClick={() => setOpenReminderModal(true)}>
                    New reminder
                  </li>
                </AtsButtonMenu>
              )
            }
          </Can>
        </div>
      </div>
      <div className="timeline__body">
        {isActivityFeed ? (
          <TimelineComment />
        ) : (
          <Reminders
            initials={initials}
            reminderData={reminderData}
            setReminderData={setReminderData}
            setOpenReminderModal={setOpenReminderModal}
          />
        )}
        {timeline.data.map((data, k) => (
          <TimelineItem key={`${k + 1}`} data={data as TimelineItemData} onAction={onActionClick} />
        ))}
        {hasMore && (
          <Button
            className="timeline__load-more"
            type={BUTTON_TYPES.TEXT}
            onClick={() => onLoadMore(false)}
            disabled={loading}
          >
            Load more
          </Button>
        )}
      </div>
      <PopupCreateInteraction
        open={newInteractionModal}
        onClose={closeInteractionPopup}
        data={dataInteraction as never}
      />
      <PopupCreateReminder
        open={openReminderModal}
        onClose={closeReminderPopup}
        data={reminderData as never}
        initials={initials}
      />
      <Popup
        open={!!deleteInteractionUuid}
        title="Delete interaction"
        cancelButtonProps={{ onClick: () => setDeleteInteractionUuid(null), children: BUTTON_TEXT.NO }}
        okButtonProps={{
          onClick: () => {
            dispatch(deleteInteraction(deleteInteractionUuid as Uuid, id));
            setDeleteInteractionUuid(null);
          },
          children: BUTTON_TEXT.YES,
        }}
      >
        Are you sure you want to delete interaction?
      </Popup>
    </Card>
  );
};
